import type { Secret } from '@imprivata-cloud/dataprivacy';
import type { ConsentGrantType, ConsentStatusType, ConsentType, Entitlement } from './constants';

export type Metadata = { rawMetadata?: string; metadataUrl?: string };

export type TenantInfo = { tenantId: string; tenantType: string };

export interface IdpMetadataGetResponse {
  idpMetadataInfoList: IdpMetadataInfo[];
  domainNames: string[];
}

export interface IdpMetadataInfo {
  idpMetadataId: string;
  idpId: string;
  idpDisplayName: string;
  metadataUrl?: string;
}

export interface IdpMetadataSaveRequest {
  metadataId?: string;
  displayName: string;
  domain: string;
  metadata?: Metadata;
}

export interface MetadataSaveResponse {
  entityId: string;
  metadataId: string;
}

// IdpConfigGet
export interface IdpConfigGetResponse {
  idpConfigId: string;
  samlGroupAttrName: string;
  samlGroupAttrValues: string[];
}
export interface IdpConfigGetRequest {
  idpMetadataId: string;
}

// IdpConfigsFind
export interface IdpConfigsFindRequest {
  idpMetadataId?: string;
}
export interface IdpConfigsFindResponse {
  idpConfigs: IdpConfig[];
}

export interface IdpConfig {
  idpConfigId: string;
  idpMetadataId: string;
  samlGroupAttrName: string;
  samlGroupAttrValues: string[];
}

// IdpConfigSave
export interface IdpConfigSaveRequest {
  idpMetadataId: string;
  samlGroupAttrName: string;
  samlGroupAttrValues: string[];
}
export interface IdpConfigSaveResponse {
  idpConfigId: string;
}

// IdpConfigUpdate
export interface IdpConfigUpdateRequest {
  idpMetadataId?: string;
  idpConfigId?: string;
  samlGroupAttrName: string;
  samlGroupAttrValues: string[];
}
export interface IdpConfigUpdateResponse {
  idpConfigId: string;
}

export const BUSINESS_EMAIL_VERIFICATION_METHOD = {
  BOOTSTRAP_EMAIL_LINK: 'bootstrap_email_link',
  BOOTSTRAP_IDENTITY_INHERITANCE: 'bootstrap_identity_inheritance',
  EMAIL_OTP: 'email_otp',
} as const;
export type BusinessEmailVerificationMethod =
  (typeof BUSINESS_EMAIL_VERIFICATION_METHOD)[keyof typeof BUSINESS_EMAIL_VERIFICATION_METHOD];

export interface OrgInfoGetResponse {
  logoUrl: string;
  orgName: string;
  domains: string[];
  businessEmail: string;
  businessEmailVerifiedAt?: string;
  businessEmailVerificationMethod?: BusinessEmailVerificationMethod;
}

// All fields are optional and only the fields that are provided will be updated.
export interface OrgInfoSaveRequest {
  logoUrl?: string;
  orgName?: string;
  businessEmail?: string;
}

export interface OrgInfoSaveResponse {
  businessEmailVerifiedAt?: string;
  businessEmailVerificationMethod?: BusinessEmailVerificationMethod;
}

export type BusinessEmailVerificationRequestResponse = { otpId: string };
export type BusinessEmailVerificationConfirmResponse = {
  verifiedAt: string;
  verificationMethod: BusinessEmailVerificationMethod;
};

export interface OrgPreferencesGetResponse extends OrgPreferencesUpdateRequest {}

export interface OrgPreferencesUpdateRequest {
  logoUrl?: string;
  orgName?: string;
  usernameLabel?: string;
  passwordLabel?: string;
  proxCardLabel?: string;
}

export interface GetAadConsentRequest {
  aadTenantId: string;
}

export interface GetAadConsentResponse {
  aadConsentUrl: string;
}

export interface TenantSetupCompleteRequest {
  email: string;
  withEntraId: boolean;
  consent: ApiConsent;
}

export interface ApiConsent {
  grantType?: string; // setup rerun may not need DPA consent
  documentName: string;
}

export interface TenantLookupResponse {
  tenantIds: string[];
  tenantList: TenantInfo[];
}

export interface GetClientUserSessionResponse {
  tenantId?: string;
  tenantType?: string;
  userInfo?: UserInfo;
}

export type UserInfo = {
  username: string;
  email?: string;
  roleType: string;
  productExperienceVisitorId?: string;
  userUpn?: string;
  userDisplayName?: string;
  mfaRequiredDate?: string | null;
  mfaEnrolled?: boolean | null;
};

export interface EntraIdConsentUrlGetResponse {
  entraConsentUrl: string;
}

export interface EntraIdConsentUrlGetRequest {
  entraTenantId: string;
}

export interface EntraIdTenantSetupResponse {
  entraUserId: string;
}

export interface EntraIdAuthenticationTestRequest {
  providedName: string;
  password: Secret;
}

export interface UserIdentity {
  id: string;
  entraIdId: string;
  entraIdUpn?: string;
  adUpn?: string;
  samAccountName?: string;
  emailAddress?: string;
}

export type EntraIdAuthenticationTestResultType = 'success' | 'app' | 'system';

export interface EntraIdAuthenticationTestResults {
  code: string;
  type: EntraIdAuthenticationTestResultType;
  message: string;
  rawOutput?: Record<string, unknown>;
}

export interface EntraIdAuthenticationTestResponse {
  status: 'success' | 'failed';
  traceId: string;
  userIdentity?: UserIdentity;
  results: EntraIdAuthenticationTestResults;
}

export type OrgInfo = { name: string; logoUrl: string };

export type StartSsoLink = { startSsoUrl: string; applianceDesignator: string };

export type TrialProductKey = 'ipa';

export type ProductTrial = {
  productKey: string; // spec type is string; only the request is narrowed to TrialProductKey
  eligible: boolean;
  active: boolean;
  startedAt: string | null; // ISO-8601 date-time
  expiresAt: string | null; // ISO-8601 date-time
};

export type TrialStartRequest = { productKey: TrialProductKey };

export type TrialAgreement = {
  status: ConsentStatusType;
  documentUrl?: string;
};

export type StartedTrial = {
  productKey: string;
  active: boolean;
  offered: boolean;
  eligible: boolean;
  startedAt: string; // ISO-8601 date-time
  expiresAt: string; // ISO-8601 date-time
  agreement?: TrialAgreement;
  launchUrl?: string;
};

export type TrialStartResponse = { trial: StartedTrial };

// A 409 means a trial of this product already exists; the spec gives it no body worth reading.
export type TrialStartOutcome = TrialStartResponse | 'already-exists';

export type PurchasedProduct = {
  displayName: string;
  integrationId: string;
  productTag: string;
  spDisplayName: string;
  spMetadataId: string;
  startSsoLinks: StartSsoLink[];
  trial?: ProductTrial;
};

export type NonPurchasedProduct = {
  displayName: string;
  productTag: string;
  learnMoreUrl: string;
  trial?: ProductTrial;
};

export type ProductInfo = {
  purchasedProducts: PurchasedProduct[];
  nonPurchasedProducts: NonPurchasedProduct[];
};

export interface GetLandingPageResponse {
  orgInfo?: OrgInfo;
  productInfo: ProductInfo;
}

export type LaunchUrl = { name?: string; url: string };

export type ProductTrialV3 = {
  offered: boolean;
  eligible: boolean;
  startedAt?: string | null;
  expiresAt?: string | null;
  launchUrl?: string;
};

export type ProductV3 = {
  key: string;
  name: string;
  available: boolean;
  active: boolean;
  documentationUrl?: string;
  launchUrls: LaunchUrl[];
  spMetadata?: { id: string; displayName: string };
  trial: ProductTrialV3;
};

export interface GetLandingPageV3Response {
  orgInfo?: OrgInfo;
  productInfo: { purchasedProducts: ProductV3[]; nonPurchasedProducts: ProductV3[] };
}

export enum FileAccessModifier {
  PRIVATE = 'private',
  PUBLIC = 'public',
}

export type PrepareFileUploadResponse = { uploadUrl: string; readUrl: string };

// DPA
export interface CheckDpaConsentRequest {
  consentDocumentName: string;
}
export interface CheckDpaConsentResponse {
  actionType: CheckDpaConsentResponseActionType;
  documentName: string; // example impr-dpa-consent-v2_0.html
  documentUrl: string; // example https://file-access.app.dev.imprivata.cloud/documents/consent/impr-dpa-consent-v2_0.html
}

export enum CheckDpaConsentResponseActionType {
  NONE = 'NONE', // Don't show the clickthrough
  CHECK_NEEDED = 'CHECK_NEEDED',
  UPDATE_NEEDED = 'UPDATE_NEEDED',
}

// create token
export interface ClientCreateRequest {
  enterpriseId: string;
  installationTokenExpirationInMinutes: number;
}

export interface ClientCreateResponse {
  clientMetadata: ClientMetadata;
}

export type ClientMetadata = {
  clientId: string;
  eamEnterpriseId: string;
  installationToken: TokenSecret;
  tokenExpiryTime: number;
  createdAt: number;
  installationTokenValue: string;
};
export type TokenSecret = { data: string; origin: null; isProxied: false };

export type GroupRequest = {
  groupName: string;
  groupId: string;
  userCount: number;
  syncEnabled: boolean;
  adminEnabled: boolean;
  idpDeleted: boolean;
};

export type FindDirectoriesResponse = { directories: Directory[] };

export type Directory = {
  name: string;
  directoryId: string;
  latestSyncAt: string;
  syncedGroupCount: number;
  idpTenantId: string;
};

export type ListSelectors = {
  fields?: string[];
  sortFields?: SortField[];
  filters?: Filter[];
  search?: SearchSelector;
};

export type SortField<T = string> = { field: T; order?: SortOrder };

export type Filter = {
  field: string;
  value?: string | boolean | number;
  operator?: FilterOperator;
};

export type SearchSelector<T = string> = { pattern: string; fields?: T[] };

export enum SortOrder {
  ASC = 'asc',
  DESC = 'desc',
}

export enum FilterOperator {
  EQUALS = 'equals',
  NOT_EQUALS = 'not-equals',
  NOT_NULL = 'not-null',
}

export const ALL_ITEMS_PATTERN = '*';

export type FindUsersRequest =
  | { selectors: ListSelectors; pageSize?: number }
  | { pageToken: string; pageSize?: number };

export type DirectorySyncProgressGetRequest = { directoryId: string };

export type DirectorySyncProgressGetResponse = {
  syncStatus: SyncStatus;
  failureReason?: SyncFailureReason;
  info?: SyncProgressInfo;
  latestSyncAt?: string;
};

export enum SyncStatus {
  NOT_INITIALIZED = 'not-initialized',
  RUNNING = 'running',
  SUCCEEDED = 'succeeded',
  FAILED = 'failed',
}

export enum SyncFailureReason {
  IDP_FAILURE = 'idp-failure',
  SYNC_ALREADY_RUNNING = 'sync-already-running',
  SYNC_INTERRUPTED = 'sync-interrupted',
  SYSTEM_FAILURE = 'system-failure',
  TRANSIENT_IDP_FAILURE = 'transient-idp-failure',
  TRANSIENT_SYSTEM_FAILURE = 'transient-system-failure',
}

export type SyncProgressInfo = {
  directoryId: string;
  syncId: string;
  totalStepsProcessed: number;
  totalSteps: number;
};

export type DirectoryFailedSyncFindRequest = { directoryId: string };

export type DirectoriesFailedSyncFindResponse = { info: FailedSyncsFindInfo[] };

export type FailedSyncsFindInfo = {
  syncId: string;
  failureReason: string;
  createdAt: string;
  endedAt?: string;
};

export type FindUsersResponse = {
  users: User[];
  totalCount: number;
  latestUpdatedAt: string;
  pagingInfo?: PagingInfo;
};

export type User = {
  userId: string;
  displayName: string;
  upn: string;
  tenantId: string;
  idpTenantId: string;
  idpUserId: string;
  roleType: string;
  firstName?: string;
  lastName?: string;
  mobilePhone?: string;
  email?: string;
  lockedUntil?: string; // ISO 8601 date-time
  factorLockouts?: FactorLockout[];
  idpUserEnabled: boolean;
  createdAt: string; // ISO 8601 date-time
  updatedAt: string; // ISO 8601 date-time
};

export type PagingInfo = { afterToken: string; beforeToken: string };

export interface GroupForSyncMarkRequest {
  groupId: string;
  isAdmin: boolean;
}

export interface GroupForSyncUnmarkRequest {
  groupId: string;
}

export interface GroupsFindResponse {
  groups: Group[];
  totalCount: number;
  latestUpdatedAt: string;
  pagingInfo: PagingInfo;
}

export interface Group {
  groupName: string;
  groupId: string;
  userCount: number;
  syncEnabled: boolean;
  adminEnabled: boolean;
  idpDeleted: boolean;
}

export interface FindListRequest {
  selectors: {
    search?: { pattern: string; fields: string[] };
    filters: { field: string; value: string; operator: string }[];
  };
}

export interface DomainsValidateRequest {
  names: string[];
}

export interface DomainVerifyResponse {
  otpId?: string;
}
export interface DomainRegisterRequest {
  domainName: string;
  otpId: string;
  otp: Secret;
}

export interface AuthenticatorsFindByUserIdsRequest {
  userIds: string[];
}

export interface AuthenticatorsFindByUserIdsResponse {
  authenticators: Authenticator[];
}

export interface AuthenticatorsUnenrollRequest {
  authenticatorIds: string[];
}

export interface CredentialsFindRequest {
  userId: string;
}
export interface CredentialsFindResponse {
  credentialInfo: CredentialInfo[];
}

export interface CredentialInfo {
  credentialId: string;
  username: string;
  appId: string;
  appDisplayName: string;
}

export interface CredentialDeleteRequest {
  credentialId: string;
}

export enum FactorType {
  FACE = 'face',
  FIDO = 'fido',
  FINGERPRINT = 'fingerprint',
  PROX = 'prox',
  PASSWORD = 'pwd',
  PIN = 'impr-pin',
  USERNAME = 'username',
  TEMP_CODE = 'temp-code',
  IMPRIVATA_ID = 'impr-id',
  LEARNED_CREDS = 'learned-creds',
  OTP = 'email-otp',
}

export interface Authenticator {
  authenticatorId: string;
  userId: string;
  shortName: string;
  deviceModel?: string;
  lastUsedAt: string;
  factorType: FactorType;
  assuranceType: AssuranceType;
}

export enum AssuranceType {
  UNSPECIFIED = 'unspecified',
  IAL2_ASSURED = 'ial2-assured',
}

export interface ConsentInfo {
  actionType: CheckDpaConsentResponseActionType;
  consentType?: ConsentType; // @deprecated v1
  grantType: ConsentGrantType;
  statusType?: ConsentStatusType;
  /** RFC 3339 date-time the consent lapses, e.g. `2027-07-31T13:25:04.01413Z`. */
  expiresAt?: string;
  documentMetadataList: Pick<
    ConsentDocumentMetadata,
    'documentName' | 'documentPurpose' | 'documentSource'
  >[]; // v2
  residenceState?: string; // @deprecated v1
  documentName?: string; // @deprecated v1
  documentUrl?: string; // @deprecated v1
}
export interface SelfServiceLandingPageDataResponse {
  orgInfo: { name: string; logoUrl: string };
  consentInfos: ConsentInfo[];
  authenticators: Authenticator[];
  credentials: CredentialInfo[];
  userInfo: { userId?: string; directoryId?: string; username?: string; externalId?: string };
  residenceLocations?: ResidenceLocation[];
  enabledFactors: string[];
}

export interface ConsentSubmitResponse {
  grantType: ConsentGrantType;
  documentName: string;
  residenceState?: string;
  consentType: ConsentType;
  userId?: string;
}

export interface ConsentSubmitRequest {
  consentType: ConsentType;
  grantType: ConsentGrantType;
  documentName: string;
  userId?: string;
  residenceState?: string;
}

export interface TenantInfoResponse {
  currentTenant: { entitlements: Entitlement[] };
  relatedTestTenants: { tenantId: string }[];
}

export interface SpDisplayNameUpdateRequest {
  spMetadataId: string;
  spDisplayName: string;
}

export interface SpDisplayNameUpdateResponse {
  spMetadataId: string;
}

export type ImprDomain = { domainName: string; isPrimary: boolean };

export type ImprDir = {
  directoryId?: string;
  name?: string;
  description?: string;
  type?: number;
  domains?: ImprDomain[];
};

export interface ImprUserSaveRequest {
  user: ImprUser;
}

export interface ImprUserSaveResponse {
  userId: string;
}

export type ImprUserDeleteRequest = { userId: string };

export interface ImprDirResponse {
  directories: ImprDir[];
}

export type ImprUserRole = {
  roleId?: string;
  value: ImprUserRoleValue;
  displayName?: string;
  action?: Action;
  isPrimary?: boolean;
};

export enum ImprUserRoleValue {
  admin = 'admin',
  user = 'user',
}

export type GrpMembership = { groupId: string; displayName?: string };

export type FactorLockout = { factorType: string; lockedUntil: string };

export type ImprUser = {
  userId?: string;
  directoryId?: string;
  username: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  userType?: string;
  roles?: ImprUserRole[];
  userGroupMembership?: GrpMembership[];
  emails: ImprUserEmail[];
  phoneNumbers?: ImprPhoneNumber[];
  lockedUntil?: string;
  factorLockouts?: FactorLockout[];
};

export function findRole(role: ImprUserRoleValue, roles: ImprUserRole[] | undefined) {
  return roles?.find(r => r.value === role);
}

export function isAdmin(user: ImprUser | undefined) {
  return !!findRole(ImprUserRoleValue.admin, user?.roles);
}

export enum Action {
  ADD = 'ADD',
  REMOVE = 'REMOVE',
}

export const addAdminRoleTemplate: ImprUserRole = {
  action: Action.ADD,
  value: ImprUserRoleValue.admin,
  displayName: '',
  isPrimary: true,
};

export function buildAddUserRole(isPrimary: boolean): ImprUserRole {
  return { action: Action.ADD, value: ImprUserRoleValue.user, displayName: '', isPrimary };
}

export type ImprUserEmail = {
  emailId?: string;
  value: string;
  isPrimary: boolean;
  action?: Action;
};

export type ImprPhoneNumber = {
  value: string;
  phoneNumberId?: string;
  type?: string;
  displayName?: string;
  isPrimary?: boolean;
  action?: Action;
};

export type PageInfo = { count: number; startIndex: number };

export interface ImprIdpUsersFindRequest {
  pageInfo?: PageInfo;
}

export interface ImprIdpUsersFindResponse {
  usersTotal: number;
  users: ImprUser[];
  pageInfo: PageInfo;
}

export interface IdpGetUserRequest {
  userId: string;
}

export interface IdpGetUserResponse {
  user: ImprUser;
}

export type InitialUser = { username: string; password: Secret; email?: string };

export interface DirectoryAddRequest {
  directory: ImprDir;
  initialUser?: InitialUser;
}

export interface DirectoryAddResponse {
  directoryId: string;
  userId?: string;
  verified: boolean;
  otpId?: string;
}

export type AdminEmailConfirmRequest = {
  userId: string;
  otpId: string;
  otp: Secret;
  email: string;
};
export type AdminEmailConfirmResponse = { verified: boolean };

export interface InstallationToken {
  data: string;
}
export interface ClientCredential {
  clientCredentialId: string;
  clientMetadata?: ClientMetadata;
  installationToken: InstallationToken;
  installationTokenExpiry: number;
  createdAt: number;
  integrationId?: string;
  installationTokenValue: string;
}

export enum ClientCredentialClientType {
  PAS = 'pas',
  AIP = 'aip',
}

export interface CreateClientCredentialRequest {
  clientType: ClientCredentialClientType;
  clientMetadata?: ClientMetadata;
  installationTokenExpirationInMinutes: number;
  accessTokenExpirationInMinutes: number;
  integrationId?: string;
}

export interface CreateClientCredentialResponse {
  clientCredential: ClientCredential;
}

export type ImprDirectory = {
  name?: string;
  description?: string;
  directoryId?: string;
  type?: number;
  domains?: ImprDomain[];
};

export type ImprDirectoriesConfig = {
  cidrNotations: string[];
  imprDomainSuffix: string; // e.g. imprivata.id, dev.imprivata.id
};

export type ImprIdpDataGetResponse = {
  directories: ImprDirectory[];
  directoriesConfig: ImprDirectoriesConfig;
};

export type UserPasswordChangeRequest = { newPassword: Secret };

export type SettingWithConstraints = { value: number; max: number; min: number };

export enum DocumentPurpose {
  CONSENT = 'biometric-consent',
  NOTICE = 'biometric-notice',
}
export interface PrepareUploadRequest {
  filename: string;
  purpose: DocumentPurpose;
}

export interface PrepareUploadRespone {
  readUrl: string;
  uploadUrl: string;
  operationId: string;
}

export interface ResidenceLocationsSaveRequest {
  locations: string[];
}

export interface ResidenceLocationsSaveResponse {
  savedCount: number;
}
interface CustomerAgreementInfo {
  documentFilename: string;
  readUrl: string;
  documentPurpose: DocumentPurpose;
  documentSource: 'imprivata' | 'customer';
  requiredResidenceLocations: string[];
}
export interface ResidenceLocation {
  shortName: string;
  fullName: string;
  displayName: string;
}

export interface ConsentDocumentMetadata {
  documentName: string;
  readUrl: string;
  documentPurpose: 'biometric-consent' | 'biometric-notice';
  documentSource: 'customer' | 'imprivata';
  requiredResidenceLocations: ResidenceLocation[];
}

export interface UserConsentCheckRequest {
  userId?: string;
  userUpn?: string;
  consentType: string;
}

export interface UserConsentCheckResponse {
  actionType: string;
  grantType: string;
  documentMetadata: ConsentDocumentMetadata[];
}

export interface UserConsentSubmitRequest {
  consent: {
    consentType: ConsentType;
    userId: string;
    residenceLocationShortName: string;
    grantType: string;
    documentNameList: string[];
  };
  userUpn?: string;
  userDisplayName?: string;
}

export interface CustomerAgreementInfoGetResponse {
  customerAgreementInfoList: CustomerAgreementInfo[];
  residenceLocationList: ResidenceLocation[];
  customAgreementEnabled: boolean;
  customerMandatoryResidenceLocationList: ResidenceLocation[];
}

export interface LegalAgreementDeleteRequest {
  documentPurpose: DocumentPurpose;
}

type ConsentRevoke = Pick<
  ConsentDocumentMetadata,
  'documentName' | 'documentPurpose' | 'documentSource'
>;

export interface UserConsentRevokeRequest {
  consentRevokeList: ConsentRevoke[];
}

export type TempCodeAssignRequest = { userId: string; data?: Record<string, unknown> };

export type TemporaryCode = { data: string; origin?: string; isProxied?: boolean };

export type TempCodeAssignResponse = { tempCode: TemporaryCode };

export type TemporaryCodeMetadataGetRequest = { userId: string };

export type TemporaryCodeMetadataGetResponse = {
  userId: string;
  username: string;
  multiUse?: boolean;
  createdAt?: string;
  startsAt?: string;
  expiresAt?: string;
  authenticatorId: string;
};

export type SettingsGetRequest = { featureKey: string; categoryName: string; instanceKey?: string };

export type SettingsGetResponse = { settings: Setting[] };

export type SettingsUpdateRequest = { settings: Setting[] };

export type Setting = {
  settingId?: string;
  featureKey: string;
  configType: string;
  categoryName: string;
  instanceKey?: string;
  name: string;
  valueType: string;
  value: string;
};

export type UserUnlockRequest = { userId: string };

export type FactorLockoutClearRequest = { userId: string; factorTypes: string[] };

export type ImportFileSubmitRequest = { importFile: File };

export type ImportStatus = 'COMPLETED' | 'FAILED';

export type ImportResult = {
  totalRows: number;
  successfulRows: number;
  failedRows: number;
  status: ImportStatus;
};

export type ImportFileSubmitResponse = { importResult: ImportResult; errorReport?: Blob };

export type UsersInviteRequest = { userIds: string[] };

export type UsersInviteResponse = {
  requestedInvitationsCount: number;
  scheduledInvitationsCount: number;
  rejectedInvitationsCount: number;
  rejectedInvitations?: RejectedInvitation[];
};

export type RejectedInvitation = { userId: string; reason: string };

export type ActivityReportGetRequest = { startDate?: string; endDate?: string };
export type LoginActivityReportGetRequest = ActivityReportGetRequest;
export type EpcsAuditReportGetRequest = ActivityReportGetRequest;
// Both bounds are required ISO 8601 date-times applied in UTC, and the span between
// them cannot exceed 30 days - the picker enforces that limit before we get here.
export type EnrollmentActivityReportGetRequest = { startTimestamp: string; endTimestamp: string };

// Password Policy Configuration
// Matches OpenAPI spec: saas-api-idls/openapi/am-portal-config/v1/openapi/spec.yaml v1.2.1

export interface IntegerConstraints {
  min: number;
  max: number;
}

export interface IntegerSetting {
  value: number;
  constraints: IntegerConstraints;
}

export interface BooleanSetting {
  value: boolean;
}

export interface PasswordPolicyGetResponse {
  minimumLength: IntegerSetting;
  maximumLength: IntegerSetting;
  requireAlphabetic: BooleanSetting;
  requireNumeric: BooleanSetting;
  requireSpecial: BooleanSetting;
  requireLowercase: BooleanSetting;
  requireUppercase: BooleanSetting;
  passwordReuse: IntegerSetting;
  passwordExpiration: IntegerSetting;
}

export interface PasswordPolicyUpdateRequest {
  minimumLength: number;
  maximumLength: number;
  requireAlphabetic: boolean;
  requireNumeric: boolean;
  requireSpecial: boolean;
  requireLowercase: boolean;
  requireUppercase: boolean;
  passwordReuse: number;
  passwordExpiration?: number;
}

export interface AuthenticationStrengthGetRequest {
  applicationId: string;
}

export interface AuthenticationStrengthGetResponse {
  currentValue: string;
  availableValues: string[];
}

export interface RuleCapabilities {
  system?: boolean;
  conditionLocked?: boolean;
  valueEditable?: boolean;
  enabled?: boolean;
}

export interface SubRule {
  ruleId?: string;
  exprType: string;
  expr?: unknown;
  capabilities?: RuleCapabilities;
  allowedValues?: string[];
  value?: string;
}

export interface ValueRule {
  version: string;
  subRules: SubRule[];
}

export interface RuleResource {
  ruleId: string;
  instanceKey: string;
  version: number;
  createdAt: string;
  updatedAt: string;
  valueRule: ValueRule;
}

export interface RulesFindRequest {
  instanceKey: string;
}

export interface RulesGetResponse {
  rules: RuleResource[];
}

export interface RulesUpdateRequest {
  instanceKey: string;
  version: number;
  valueRule: ValueRule;
}

export interface RiskLevelOption {
  token: string;
  label: string;
}

export interface RuleSetItem {
  id: string;
  name: string;
}

export interface RuleSetsGetResponse {
  ruleSets: RuleSetItem[];
}

export interface RbaRuleSet {
  id: string;
  name: string;
}

export interface RbaPolicyGetResponse {
  rbaEnabled: boolean;
  ruleSet?: RbaRuleSet | null;
}

export type RbaPolicySetRequest =
  | { rbaEnabled: false; ruleSetId?: string; ruleSetName?: string }
  | { rbaEnabled: true; ruleSetId: string; ruleSetName: string };

// Matches OpenAPI spec: am-portal-config/v1 — ProxCardMaxNumberGetResponse / ProxCardMaxNumberUpdateRequest
export interface ProxCardMaxNumberGetResponse {
  proxCardMaxNumber: number;
}

export interface ProxCardMaxNumberUpdateRequest {
  proxCardMaxNumber: number;
}

// Matches OpenAPI spec: am-portal-svc/config/v1 — FaceDuplicatePreventionGetResponse / FaceDuplicatePreventionUpdateRequest
export interface FaceDuplicatePreventionGetResponse {
  faceDuplicatePreventionEnabled: boolean;
}

export interface FaceDuplicatePreventionUpdateRequest {
  faceDuplicatePreventionEnabled: boolean;
}

export type SupervisedAssuranceCancelRequest = { assuranceId: string };

export type SupervisedAssuranceStartRequest = { authenticatorId: string };

export type SupervisedAssuranceStartResponse = { assuranceId: string };

export type IdentityVerificationRecordRequest = {
  assuranceId: string;
  idDocuments: string[];
  comments?: string;
};

/**
 * One authentication factor bound to the authenticator being assured. `authenticatorId` is required for
 * `impr-id` and ignored for `pwd`, so the discriminant carries it; `systemData` is `impr-id` only.
 */
export type AuthenticatorVerifyRequest =
  | { assuranceId: string; factorType: FactorType.PASSWORD; factorData: Secret }
  | {
      assuranceId: string;
      factorType: FactorType.IMPRIVATA_ID;
      authenticatorId: string;
      factorData: Secret;
      systemData?: Secret;
    };

export type SupervisedAssuranceCompleteRequest = {
  assuranceId: string;
  oidcAuthorizationCode: string;
  oidcCodeVerifier: string;
};

export enum CorrectionDestination {
  USER_SETUP = 'USER_SETUP',
  EXTERNAL_DIRECTORY = 'EXTERNAL_DIRECTORY',
}

export type ChannelInfo = {
  /** Held value for this channel, non-editable. Empty means unheld — the caller may supply one. */
  value: string;
  /** Present only when `value` is non-empty: where to direct a clinician to correct a wrong value. */
  correctionDestination?: CorrectionDestination;
};

export type VerificationChannels = { email: ChannelInfo; phone: ChannelInfo };

export type IndividualAssuranceStartRequest = { authenticatorId: string };
export type IndividualAssuranceStartResponse = {
  assuranceId: string;
  verificationChannels: VerificationChannels;
};

export type IndividualAssuranceCancelRequest = { assuranceId: string };

export type IndividualAssuranceCompleteRequest = { assuranceId: string };

export type IdProofingSessionStartRequest = { assuranceId: string };
export type IdProofingSessionStartResponse = { idProofingSessionToken: string; url: string };

export enum IdProofingStatus {
  SUCCEEDED = 'succeeded',
  FAILED = 'failed',
  EXPIRED = 'expired',
}
export type IdProofingStatusGetRequest = { assuranceId: string; idProofingSessionToken: string };
export type IdProofingStatusGetResponse = { idProofingStatusType: IdProofingStatus };

export type OtpSendByEmailRequest = { assuranceId: string; emailAddress: string };
export type OtpSendByEmailResponse = { otpId: string };

export type OtpSendBySmsRequest = { assuranceId: string; mobileNumber: string };
export type OtpSendBySmsResponse = { otpId: string };

export type OtpVerifyRequest = { assuranceId: string; otpId: string; otp: Secret };

export type PasswordExpirationInfoGetRequest = Record<string, never>;

export type PasswordExpirationInfoGetResponse = { passwordExpiresAt?: string };

export type EmailVerificationRequest = Record<string, never>;
export type EmailVerificationResponse = { otpId: string };

export type VerificationOtpRequest = { otpId: string; otp: Secret };
export type VerificationOtpResponse = { verifiedAt: string };

// Applications / Epic Hyperdrive
export interface IdpCertificate {
  publicKey: string;
  idpCertificate: string;
}

export interface AppIntegration {
  appIntegrationId: string;
  appName: string;
  appDisplayName: string;
  idpIssuer: string | null;
  idpCertificates: IdpCertificate[];
  createdAt: string;
  updatedAt: string;
}

export interface AppIntegrationFindByNameRequest {
  appName: string;
}

export interface AppIntegrationFindByNameResponse {
  appIntegration: AppIntegration;
}

export interface EpicAppLockConfig {
  sharedWorkstation?: string;
  privateWorkstation?: string;
}

export enum WorkstationType {
  SHARED = 'shared',
  PRIVATE = 'private',
}

export interface AppConfig {
  samlEnabled: true;
  appUsernameAdMatch: boolean | null;
  interconnectBaseUrls: string[] | null;
  captureMaxRetries: number;
  epicAppLockConfig?: EpicAppLockConfig;
}

export interface AppConfigFindRequest {
  applicationId: string;
}

export interface AppConfigGetResponse {
  appConfig: AppConfig;
  redirectUri: string;
}

export interface AppConfigUpdateRequest {
  applicationId: string;
  appConfig: AppConfig;
}

// OIDC Connections (generic, IDV is one type)
export enum OidcConnectionType {
  IDV = 'idv-integration',
  // Future: SSO, etc.
}

export enum IdvConnectionTemplate {
  EPIC_CARE_LINK = 'epic-carelink',
  SERVICE_NOW = 'service-now',
  CUSTOM = 'custom',
}

export enum IdvAuthMethod {
  CLIENT_SECRET_POST = 'client_secret_post',
  PRIVATE_KEY_JWT = 'private_key_jwt',
}

export enum IdvScope {
  // `openid` is required by the backend on every Custom connection (it rejects a scope list
  // that omits it) and is implicit in the UI — never a user-selectable checkbox. It is
  // prepended when the create/update payload is built and stripped on read. The optional
  // scopes are no longer enumerated here: they come from the tenant's OIDC discovery doc
  // (`scopes_supported`) as plain strings, so scope arrays are typed `string[]`.
  OPENID = 'openid',
}

export enum IdvIalLevel {
  IAL1 = 'ial1',
  IAL2 = 'ial2',
}

// This is the base info shared across create, get, find, and update responses
export interface OidcIntegrationInfo {
  clientId: string;
  displayName: string;
  // Custom connections come back with an empty templateType (the BFF has no "custom" template —
  // empty means "no template"). `normalizeConnection` in useIdvConnections resolves that empty
  // value to IdvConnectionTemplate.CUSTOM before it reaches the UI.
  templateType: IdvConnectionTemplate | '';
  integrationType: OidcConnectionType; // Identifies the OIDC IdP flow
  redirectUris: string[];
  jwksUri?: string; // Present only for service-now and custom (private_key_jwt)
  // Custom only. Names match the BFF OIDC-integrations contract (OAuth2/OIDC standard
  // parameter names): `scope`, `tokenEndpointAuthMethod`, `identityAssuranceLevel`.
  tokenEndpointAuthMethod?: IdvAuthMethod;
  // Scopes granted to the connection. Typed as `string[]` (not the `IdvScope` enum) because
  // the tenant's OIDC discovery doc may advertise scopes beyond the ones we ship copy for
  // (e.g. `address`). `openid` is always included on the wire; the UI treats it as implicit.
  scope?: string[];
  identityAssuranceLevel?: IdvIalLevel;
}

export type IdvConnectionSummary = OidcIntegrationInfo;
export type IdvConnection = Omit<OidcIntegrationInfo, 'templateType'> & {
  // Narrowed from the base's `IdvConnectionTemplate | ''`: an IdvConnection is the UI-facing
  // shape produced after `normalizeConnection` has resolved the empty (Custom) wire value.
  templateType: IdvConnectionTemplate;
  // UI-only fields (fetched from separate resource, NOT in OpenAPI spec)
  authorizationEndpointUrl?: string;
  tokenEndpointUrl?: string;
};

export interface IdvConnectionsFindResponse {
  oidcIntegrations: OidcIntegrationInfo[];
}

// CreateRequest is OidcIntegrationInfo without clientId (server-generated). `templateType` is
// required (non-null) by the BFF, but Custom connections send it as an empty string: the BFF
// has no "custom" template — an empty templateType means "no template", which it forwards as an
// empty `product_id`, exactly what the OIDC service expects for a Custom connection.
export type IdvConnectionCreateRequest = Omit<OidcIntegrationInfo, 'clientId' | 'templateType'> & {
  templateType: IdvConnectionTemplate | '';
};

// CreateResponse is OidcIntegrationInfo plus optional clientSecret (only for epic-carelink)
export interface IdvConnectionCreateResponse extends OidcIntegrationInfo {
  clientSecret?: Secret; // Secret-wrapped, present only for epic-carelink
}

export interface IdvConnectionGetRequest {
  clientId: string;
}

export interface IdvConnectionGetResponse extends OidcIntegrationInfo {}

// Matches OpenAPI spec: OidcIntegrationUpdateRequest
export interface IdvConnectionUpdateRequest {
  clientId: string;
  displayName?: string;
  redirectUris?: string[];
  jwksUri?: string;
  // Custom only — editable post-create. Auth method is immutable, so it is intentionally
  // absent here (the BFF update contract has no tokenEndpointAuthMethod field).
  scope?: string[];
  identityAssuranceLevel?: IdvIalLevel;
}

export interface IdvConnectionUpdateResponse extends OidcIntegrationInfo {}

export interface IdvConnectionDeleteRequest {
  clientId: string;
}

export type IdvConnectionDeleteResponse = Record<string, never>;

// Standard OIDC discovery document (`/.well-known/openid-configuration`). Only the
// fields the UI consumes are typed; the index signature keeps the rest accessible.
export interface OidcDiscoveryResponse {
  issuer?: string;
  authorization_endpoint: string;
  token_endpoint: string;
  // Scopes the IdP advertises support for. Drives the Custom form's scope checkboxes
  // (minus the always-required `openid`). Optional: a document may omit it.
  scopes_supported?: string[];
  [key: string]: unknown;
}

// OIDC App Integrations Admin (oidc-applications-admin v1, EAMC-3327)

export interface OidcAppIntegration {
  clientId: string;
  redirectUris: string[];
  tokenEndpointAuthMethod: string;
  productId: string;
}

export interface OidcAppIntegrationCreateRequest {
  displayName: string;
  redirectUris: string[];
  productId: string;
}

export interface OidcAppIntegrationCreateResponse {
  clientId: string;
  clientSecret: Secret;
  redirectUris: string[];
  tokenEndpointAuthMethod: string;
  productId: string;
}

export interface OidcAppIntegrationUpdateRequest {
  clientId: string;
  redirectUris: string[];
}

export interface OidcAppIntegrationUpdateResponse {
  clientId: string;
  redirectUris: string[];
  tokenEndpointAuthMethod: string;
  productId: string;
}

export interface OidcAppIntegrationFindRequest {
  oidcIdpFlow: string;
  productId: string;
}

export interface OidcAppIntegrationFindResponse {
  oidcAppIntegrations: OidcAppIntegration[];
}

export interface OidcAppIntegrationDeleteRequest {
  clientId: string;
}

export type OidcAppIntegrationDeleteResponse = Record<string, never>;

// Endpoints management (LYRA-1744)

// Agent liveness. Consumers MUST branch on this before relying on the optional
// agent-info fields, which are absent for group-synced endpoints with no agent.
export enum AgentStatus {
  UNSPECIFIED = 'UNSPECIFIED',
  UNKNOWN = 'UNKNOWN',
  ONLINE = 'ONLINE',
  OFFLINE = 'OFFLINE',
}

export type EndpointDeviceWithAgentInfo = {
  endpointUuid: string;
  agentStatus: AgentStatus;
  hostname?: string;
  version?: string;
  autoUpdateEnabled?: boolean;
  createdAt?: string; // ISO 8601 date-time; install date
  updatedAt?: string; // ISO 8601 date-time; record-touched, not agent report
  lastReportedAt?: string; // absent when agentStatus == UNKNOWN
  groupIds?: string[]; // synced and manual groups; absent/empty when in none
};

export type EndpointDevicesFindRequest = FindUsersRequest;

export type EndpointDevicesFindResponse = {
  endpointDevices: EndpointDeviceWithAgentInfo[];
  totalCount: number;
  latestUpdatedAt?: string;
  pagingInfo?: PagingInfo;
};

// Endpoint groups (admin-web endpoint-group v2). Synced groups are read-only;
// manual groups are created, deleted and populated by administrators.
export enum EndpointGroupSource {
  UNSPECIFIED = 'UNSPECIFIED',
  IDP_SYNCED = 'IDP_SYNCED',
  MANUAL = 'MANUAL',
}

export type EndpointGroup = {
  groupId: string;
  source: EndpointGroupSource;
  displayName: string;
  memberCount: number;
  createdAt: string;
  updatedAt: string;
};

export type EndpointGroupsFindRequest = { source?: EndpointGroupSource };

export type EndpointGroupsFindResponse = { endpointGroups: EndpointGroup[] };

export type EndpointGroupCreateRequest = { displayName: string };

export type EndpointGroupCreateResponse = { endpointGroup: EndpointGroup };

export type EndpointGroupDeleteRequest = { groupId: string };

// An empty `groupId` removes the endpoints from every manual group.
export type EndpointGroupAssignRequest = { groupId: string; endpointUuids: string[] };

// Workstation settings (admin-web workstation-settings v1). Setting ids, section ids and
// valueSource are open sets: an unknown value must still render, so they stay strings here.
export type WorkstationGroup = { groupId: string; name: string; workstationCount: number };

export type WorkstationSettingOptions = {
  values: string[];
  allowCustom: boolean;
  customRange?: { min: number; max: number };
};

// Every value is a string: a code, decimal seconds for a duration, or "" where the catalog allows it.
export type WorkstationSetting = {
  id: string;
  effectiveValue: string;
  valueSource: string;
  defaultValue?: string;
  options: WorkstationSettingOptions;
  actions: { edit: boolean; reset: boolean };
};

export type WorkstationSettingsSection = { id: string; settings: WorkstationSetting[] };

export type WorkstationSettingsView = {
  groups: WorkstationGroup[];
  sections: WorkstationSettingsSection[];
};

export type WorkstationSettingsViewGetRequest = { groupId?: string };

export type WorkstationSettingsViewGetResponse = { view: WorkstationSettingsView };

export type WorkstationSettingUpdateRequest = {
  groupId?: string;
  settingId: string;
  value: string;
};

export type WorkstationSettingResetRequest = { groupId: string; settingId: string };

export type WorkstationSettingResponse = { setting: WorkstationSetting };

export type EndpointInstallerData = {
  endpointInstallerUrl: string;
  version?: string; // blank until manifest-resolver flag on; never render as "0"
  releaseDate?: string; // blank until manifest-resolver flag on; never render as epoch
};

export type EndpointInstallationKey = {
  installationKey: Secret; // encrypted; decrypted client-side
  expirationDate: string; // ISO 8601 date-time
};

export type ConfigGetRequest = { featureKey: string; configType: string };

export type ConfigGetResponse = {
  policies: unknown[];
  policyRules: unknown[];
  settings: Setting[];
  categoryMetadata: unknown[];
  settingMetadata: unknown[];
};

export enum SettingChangeType {
  UPSERT = 'upsert',
  DELETE = 'delete',
}

export type SettingChange = { changeType: SettingChangeType; setting: Setting };

export type CustomSettingsChangeRequest = { changes: SettingChange[] };

export interface PortalDomainVerifyRequest {
  domainName: string;
  email: string;
}

export interface PortalDomainRegisterRequest {
  domainName: string;
  otpId: string;
  otp: Secret;
}

// Application Profiles (admin-web-app-profiles v1)
export interface AppProfile {
  appProfileId: string;
  machineName: string;
  displayName: string;
  createdAt: string;
  updatedAt?: string;
}

export interface AppProfilesGetResponse {
  appProfiles: AppProfile[];
}

export interface AppProfilesImportRequest {
  readUrl: string;
}

export interface AppProfilesDeleteRequest {
  appProfileIds: string[];
}
